import { isEntityKycCustomer } from "../../../../shared/kycAuthentication";
import React, { useContext, useEffect, useRef, useState } from "react";

import { addDoc, collection, doc, limit, query, where } from "firebase/firestore";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Accordion, ActionIcon, Alert, Group, Loader, Tabs, Text, Title } from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";

import { TagBadge } from "../../components/tag_badge";
import { History } from "../../components/history";
import { CustomerKYC } from "./customer_kyc";
import { CompanySendKycLinkButton } from "../../components/company_send_kyc_link_button";
import { CustomerCompanyRegistry, CustomerInfo } from "./customer_info";
// import { Orders } from "./orders";
import { CustomClaims, Customer, CustomerFundData, CustomerSummaryDoc, DocType, KfhTradeAccount, Order, OrderStatusCode, Portfolio } from "../../types";
import { useFirestore } from "../../helpers/firebaseContext";
import useFirestoreDoc from "../../helpers/useFirestoreDoc";
import OrdersInstantSearch from "../../components/orders_instant_search";
import ServicesInstantSearch from "../base_service/services_instant_search";
import TransactionsInstantSearch from "../../components/transactions_instant_search";
import CustomerUpdateRequests from "./customer_update_requests";
import CustomerWhatsapp from "./customer_whatsapp";
import CustomerRelationship from "./customer_relationship";
import { RefreshButton, RefreshButtonRef } from "../../components/customer_instant_search";
import { RenderActions } from "../orders/orders";
import useFirestoreCollection from "../../helpers/useFirestoreCollection";
import PositionBadges from "../../components/position_badges";
import { MyUserDataContext, PresenceContext } from "../../contexts";
import AskForConsent from "../../components/ask_for_consent";
import { useSessionStorage } from "../../helpers/useStorage";
import { CurrentViewers } from "../../components/current_viewers";



//create CustomerInfo component
export default function CustomerMain(props: any) {
  const { id } = useParams();

  const db = useFirestore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const refreshRef = useRef<RefreshButtonRef | null>(null);


  const [activeTab, setActiveTab] = React.useState<string | null>("kyc");
  const [openRequestSections, setOpenRequestSections] = useState<string[]>(["orders"]);
  // const [needsConsent, setNeedsConsent] = useState(true)
  const [lastViewedTabs, setLastViewedTabs] = useSessionStorage(`lastViewedTabs-${id}`, '{"positions":null}');
  const [metaTypes, setMetaTypes] = useState<DocType[]>([]);

  const userContext = useContext(MyUserDataContext);
  const presenceContext = useContext(PresenceContext);

  let userClaims = userContext.firebaseTokenResult?.claims.customClaims as CustomClaims;

  //WhatsApp chat is limited to client relations + admins for now (matches firestore rules)
  const canUseWhatsapp = !!(userClaims?.admin || userClaims?.cr);

  //Relationship profile (RM personal intelligence) is admin-only (matches firestore rules)
  const canUseRelationship = !!userClaims?.admin;

  const customerDataDoc = doc(db, "customers", id as string);
  const { loading: customerLoading, data: customerData } = useFirestoreDoc<Customer>(customerDataDoc, true);
  //If it's a KFH RM user and the customer didn't consent to access RM data, show the consent page
  const needsConsent = userClaims["kfh_rm"] && !customerData?.agreements?.kfh_rm_access_consent
  const isCompanyCustomer = !!customerData && isEntityKycCustomer(customerData);

  const positionsVisible = !customerData?.employee || userClaims?.hr === true;
  const requestedTab = searchParams.get("tab");
  const requestedPortfolio = searchParams.get("portfolio");

  useEffect(() => {
    const allowedTabs = new Set(["kyc", "history", "requests"]);

    if (positionsVisible) {
      allowedTabs.add("positions");
    }

    if (isCompanyCustomer) {
      allowedTabs.add("companyRegistry");
    }

    if (canUseWhatsapp) {
      allowedTabs.add("whatsapp");
    }

    if (canUseRelationship) {
      allowedTabs.add("relationship");
    }

    //old deep links still say "orders"
    const normalizedTab = requestedTab === "orders" ? "requests" : requestedTab;

    if (normalizedTab && allowedTabs.has(normalizedTab)) {
      setActiveTab(normalizedTab);
      return;
    }

    if (requestedPortfolio && positionsVisible) {
      setActiveTab("positions");
    } else if (!positionsVisible) {
      setActiveTab(current => current === "positions" ? "kyc" : current);
    }
    // activeTab is deliberately not a dependency: this effect applies the URL
    // to the tabs, and handleTabChange writes the user's choice back to the URL.
  }, [isCompanyCustomer, canUseWhatsapp, canUseRelationship, positionsVisible, requestedPortfolio, requestedTab]);

  // if (needsConsent) {
  //   return <AskForConsent customerData={customerData!} />
  // }

  let portfolioDataQuery = query(collection(db, "customers", id as string, "portfolios"),);
  let fundDataQuery = query(collection(db, "customers", id as string, "fund_data"));
  let kfhTradeQuery = query(collection(db, "customers", id as string, "kfhtrade_accounts"));
  let customerSummariesQuery = query(collection(db, "customers", id as string, "summaries"));
  let pendingOrdersQuery = query(
    collection(db, "customers", id as string, "orders"),
    // `done` is included so a completed subscription whose position hasn't
    // synced into fund_data yet still shows as a placeholder row on positions.
    where("tags.orderStatus.code", "in", [OrderStatusCode.waiting_for_nav, OrderStatusCode.processing, OrderStatusCode.done])
  );

  // if (needsConsent) {
  //   portfolioDataQuery = query(portfolioDataQuery, limit(0))
  //   fundDataQuery = query(fundDataQuery, limit(0))
  // }
  const { loading: portfoliosLoading, data: portfoliosData } = useFirestoreCollection(portfolioDataQuery, true);
  const { loading: fundDataLoading, data: fundData } = useFirestoreCollection(fundDataQuery, true);
  const { loading: kfhTradeDataLoading, data: kfhTradeAccountsData } = useFirestoreCollection(kfhTradeQuery);
  const { loading: customerSummariesLoading, data: customerSummariesData } = useFirestoreCollection(customerSummariesQuery);
  const { loading: pendingOrdersLoading, data: pendingOrdersData } = useFirestoreCollection(pendingOrdersQuery, true);




  if (customerLoading || !customerData || portfoliosLoading || fundDataLoading || kfhTradeDataLoading || customerSummariesLoading || pendingOrdersLoading) {
    return <Loader />
  }

  const castedPortfolioData = (portfoliosData as Portfolio[]);
  const castedFundData = fundData != undefined ? (fundData as CustomerFundData[]) : []
  // KFH Trade visibility is enforced in CustomerInfo / PositionBadges (admin or CR).
  // Portfolios and funds remain available to a broader set of users.
  const castedKfhTradeAccountsData = kfhTradeAccountsData != undefined ? (kfhTradeAccountsData as KfhTradeAccount[]) : []
  const castedCustomerSummariesData = customerSummariesData != undefined ? (customerSummariesData as CustomerSummaryDoc[]) : []
  const castedPendingOrdersData = pendingOrdersData != undefined ? (pendingOrdersData as Order[]) : []
  const handleTabChange = (value: string | null) => {
    setActiveTab(value)
    // Keep the deep link in step with the selected tab so a page opened at
    // ?tab=positions (or ?portfolio=...) can still be switched away from.
    if (value) {
      setSearchParams(current => {
        const next = new URLSearchParams(current);
        next.set("tab", value);
        if (value !== "positions") next.delete("portfolio");
        return next;
      }, { replace: true });
    }
    if (value && !needsConsent) {
      if (value === "positions" || value === "relationship") {

        trackRmViews(value);
      }
    }
  }

  //create new subcollection under customer to track who viewed which tab was viewed and at what time with a 5 min cooldown
  const trackRmViews = (value: string) => {

    if (customerDataDoc) {


      const userId = userContext.user?.uid;

      if (userId) {
        const viewsCollection = collection(db, "customers", id as string, "rm_views");
        const currentDate = Date.now();
        const cooldown = 5 * 60 * 100



        //store to local storage and check when it waslastrsaved
        const lastViewedTabJson = JSON.parse(lastViewedTabs);

        const lastViewedTime: null | Date = lastViewedTabJson[value];
        //if the rm didn't view the tab in the last 5 min, create a new rm_view instance   
        if (!lastViewedTime || (lastViewedTime && (currentDate - new Date(lastViewedTime)?.getTime() > cooldown))) {

          const viewData = {

            email: userContext.user?.email,
            displayName: userContext.user?.displayName,
            userId: userId,
            tab: value,
            timestamp: new Date()
          }

          const updatedViewedTabs = { ...lastViewedTabJson, [value]: new Date() }
          setLastViewedTabs(JSON.stringify(updatedViewedTabs))
          addDoc(viewsCollection, viewData);

        }


      }
    }

  }




  const customerDisplayName = customerData.name_en || customerData.name_ar || "";
  const quoteTypesenseFilterValue = (value: string) => `\`${value.replace(/`/g, "\\`")}\``;
  const customerIDFilterValue = quoteTypesenseFilterValue(id as string);
  const typesenseHost = import.meta.env.VITE_TYPESENSE_SEARCH_HOST;
  const canSearchGeneralCollections = Boolean(typesenseHost && userContext.customSearchKey);
  const canSearchServices = Boolean(typesenseHost && userContext.servicesSearchKey);
  const unavailableSearch = (label: string) => (
    <Alert color="yellow" title={`${label} unavailable`}>
      Your account does not have the search credentials required to view this table.
    </Alert>
  );

  return (
    <>
      {/* <Tabs defaultValue="kyc" value={tabValue} onChange={(value: any) => navigate(`/customers/${id}/${value}`)}> */}
      <>
        <Group justify="space-between" align="center" mb="sm" wrap="wrap">
          <Group gap="sm" align="center">
            <ActionIcon
              variant="subtle"
              onClick={() => navigate("/customers")}
              aria-label="Back to customers"
            >
              <IconArrowLeft size="1.5rem" />
            </ActionIcon>
            {customerDisplayName && <Title order={2}>{customerDisplayName}</Title>}
          </Group>
          <Group gap="md" align="center">
            <CurrentViewers type="customers" id={id ?? ''} size="md" />
            {/* Update Company KYC Link only allow Admin and CR team */}
            {customerData && canUseWhatsapp && <CompanySendKycLinkButton customer={customerData} />}

            {customerData.crmID && (
              <Text c="blue" fw="bold">CIF: {customerData.crmID}</Text>
            )}
          </Group>
        </Group>
        <Tabs onChange={handleTabChange} value={activeTab}>
          <Tabs.List>
            {positionsVisible && <Tabs.Tab value="positions"
              // Start of Selection
              rightSection={(customerData?.agreements?.kfh_rm_access_consent || !userClaims["kfh_rm"]) && (
                <PositionBadges
                  fundData={castedFundData}
                  portfolioData={castedPortfolioData}
                  kfhTradeAccountsData={castedKfhTradeAccountsData}
                />
              )}
            >Positions</Tabs.Tab>}
            {/* <Tabs.Tab value="fundsTransactions"
          >Fund Transactions</Tabs.Tab> */}
            <Tabs.Tab
              value="kyc"
              rightSection={<TagBadge tag={customerData?.tags?.kycStatus} />}
            >
              KYC
            </Tabs.Tab>
            <Tabs.Tab value="history">History</Tabs.Tab>
            <Tabs.Tab value="requests">Requests</Tabs.Tab>
            {canUseWhatsapp && <Tabs.Tab value="whatsapp">WhatsApp</Tabs.Tab>}
            {canUseRelationship && <Tabs.Tab value="relationship">Relationship</Tabs.Tab>}
            {isCompanyCustomer && <Tabs.Tab value="companyRegistry">Company Registry</Tabs.Tab>}
            {/* <Tabs.Tab value="transactions">Transactions</Tabs.Tab> */}
          </Tabs.List>

          {positionsVisible && <Tabs.Panel value="positions">

            {needsConsent ? <AskForConsent customerData={customerData} /> : <CustomerInfo fundData={castedFundData} portfoliosData={castedPortfolioData} customerData={customerData!} kfhTradeAccountsData={castedKfhTradeAccountsData!} customerSummariesData={castedCustomerSummariesData} activePortfolioCode={requestedPortfolio} pendingOrders={castedPendingOrdersData} />}
            {/* <CustomerInfo fundData={castedFundData} portfoliosData={castedPortfolioData} customerData={customerData!} /> */}
          </Tabs.Panel>}
          {/* <Tabs.Panel value="fundsTransactions">
          <CustomerFundsTransactions fundData={castedFundData} customerData={customerData!} />
        </Tabs.Panel> */}
          <Tabs.Panel value="kyc">
            {/* Remove the consent for kyc tab (needed to access manual links) */}
            {/* Identity (name/CIF) lives in the page header; order/service embeds keep their own via showIdentityHeader default */}
            {<CustomerKYC metaTypes={metaTypes} setMetaTypes={setMetaTypes} customerData={customerData!} showButton={true} showIdentityHeader={false} showKycVersion={true} showActiveLinks={true} showGeneratePDF={true} />}
            {/* <CustomerKYC metaTypes={metaTypes} setMetaTypes={setMetaTypes} customerData={customerData!} showButton={true} showKycVersion={true} showActiveLinks={true} showGeneratePDF={true} /> */}
          </Tabs.Panel>
          <Tabs.Panel value="requests">
            {needsConsent ? <AskForConsent customerData={customerData} /> : <>

              <Accordion
                multiple
                value={openRequestSections}
                onChange={setOpenRequestSections}
                variant="separated"
              >
                <Accordion.Item value="updateRequests">
                  <Accordion.Control><Title order={4}>Update Requests</Title></Accordion.Control>
                  <Accordion.Panel>
                    {openRequestSections.includes("updateRequests") && (
                      canSearchGeneralCollections
                        ? <CustomerUpdateRequests customerID={id as string} />
                        : unavailableSearch("Update requests")
                    )}
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="orders">
                  <Accordion.Control><Title order={4}>Orders</Title></Accordion.Control>
                  <Accordion.Panel>
                    {openRequestSections.includes("orders") && (
                      canSearchGeneralCollections
                        ? <OrdersInstantSearch
                          selectable={false}
                          renderTags={true}
                          navigateDetails={(obj: any) => navigate("/orders/" + obj.id)}
                          allowShowAll={false}
                          defaultFilter={`customerObj.id:=${customerIDFilterValue}`}
                          ActionsComponent={RenderActions}
                          indexName={import.meta.env.VITE_TYPESENSE_ORDERS_COLLECTION!}
                          additionalChildren={<RefreshButton ref={refreshRef} />}
                        />
                        : unavailableSearch("Orders")
                    )}
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="portfolio-requests">
                  <Accordion.Control><Title order={4}>Portfolio Requests</Title></Accordion.Control>
                  <Accordion.Panel>
                    {openRequestSections.includes("portfolio-requests") && (
                      canSearchServices
                        ? <ServicesInstantSearch
                          customFilters={`serviceType:=portfolio && customerObj.id:=${customerIDFilterValue}`}
                          serviceType="portfolio"
                          navigateDetails={(obj: any) => navigate("/portfolios/" + obj.id, { state: obj })}
                          indexName={import.meta.env.VITE_TYPESENSE_SERVICES_COLLECTION!}
                          selectable={false}
                          renderTags={true}
                        />
                        : unavailableSearch("Portfolio requests")
                    )}
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="transactions">
                  <Accordion.Control><Title order={4}>Transactions</Title></Accordion.Control>
                  <Accordion.Panel>
                    {openRequestSections.includes("transactions") && (
                      canSearchGeneralCollections
                        ? <TransactionsInstantSearch
                          defaultFilter={`customerID:=${customerIDFilterValue}`}
                          indexName={import.meta.env.VITE_TYPESENSE_TRANSACTION_COLLECTION!}
                        />
                        : unavailableSearch("Transactions")
                    )}
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </>}

          </Tabs.Panel>

          {canUseWhatsapp && (
            <Tabs.Panel value="whatsapp">
              {needsConsent ? <AskForConsent customerData={customerData} /> : <CustomerWhatsapp customerID={id as string} />}
            </Tabs.Panel>
          )}

          {canUseRelationship && (
            <Tabs.Panel value="relationship">
              {needsConsent ? <AskForConsent customerData={customerData} /> : <CustomerRelationship customerData={customerData} />}
            </Tabs.Panel>
          )}

          <Tabs.Panel value="history">

            {needsConsent ? <AskForConsent customerData={customerData} /> : <History
              historyCollection={collection(
                db,
                "customers",
                id as string,
                "history"
              )}
            />}


          </Tabs.Panel>
          {isCompanyCustomer && (
            <Tabs.Panel value="companyRegistry">
              {<CustomerCompanyRegistry customerData={customerData!} />}
            </Tabs.Panel>
          )}
        </Tabs></>
    </>
  );
}
